import type { EditProjectRequest } from "@/services/api/api-types";
import type {
  ProjectsUsersAndGroups,
  TriyoDynamicField,
  TriyoProject,
  TriyoProjectGroupMember,
  TriyoProjectMember,
  TriyoProjectOwner,
} from "@/types/models";
import {
  type DealPersonOption,
  type DealPriority,
  normalizeRoster,
  parseDateInput,
} from "@/components/Workflow/QuickActions/CreateDeal/create-deal-helpers";
import { buildDynamicFieldsPayload } from "@/components/Workflow/QuickActions/CreateDeal/deal-dynamic-fields";
import type { DealTeamSelection } from "@/components/Workflow/QuickActions/CreateDeal/deal-team";

const PRIORITIES: DealPriority[] = ["Low", "Medium", "High", "Urgent"];

export interface EditStateSnapshot {
  name: string;
  description: string;
  start: string;
  end: string;
  priority: DealPriority;
  team: DealTeamSelection;
  dynamicFields: TriyoDynamicField[];
}

/**
 * Compact dirty-state fingerprint for the Deal editor. File contents are
 * intentionally excluded: one permitted 25 MB upload becomes roughly 33 MB of
 * base64, and serializing that value on every ordinary form change can freeze
 * an Outlook task pane. The generated/persisted id plus metadata and status are
 * sufficient to detect file additions and removals.
 */
export function editStateFingerprint(state: EditStateSnapshot): string {
  return JSON.stringify({
    name: state.name,
    description: state.description,
    start: state.start,
    end: state.end,
    priority: state.priority,
    members: [...state.team.memberKeys].sort(),
    owners: [...state.team.ownerKeys].sort(),
    dynamicFields: state.dynamicFields.map((field) => ({
      Id: field.Id,
      Data: field.Data ?? "",
      Files: (field.Files ?? []).map((file) => ({
        Id: file.Id ?? file.uid ?? null,
        Name: file.Name ?? file.name ?? "",
        MimeType: file.MimeType ?? file.extension ?? "",
        Size: file.size ?? null,
        FileStatus: file.FileStatus ?? file.status ?? null,
      })),
    })),
  });
}

export function getDealId(project: TriyoProject): number | null {
  const value = project.Id ?? project.ProjectId;
  return typeof value === "number" && value > 0 ? value : null;
}

export function toProjectDateInput(value: string | null | undefined): string {
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(value ?? "");
  return match?.[1] ?? "";
}

/**
 * Triyo.Web stores date-picker values as local-midnight Date objects. Axios
 * serializes those values to UTC ISO strings, which the API converts back to
 * the server's local calendar date before persisting it.
 */
export function toProjectDateTime(value: string): string {
  const parsed = parseDateInput(value);
  if (!parsed) throw new Error("A valid Deal date is required.");
  return parsed.toISOString();
}

function userMemberOption(member: TriyoProjectMember): DealPersonOption | null {
  const id = member.UserId ?? member.User?.UserId ?? member.User?.Id;
  if (!id) return null;
  return {
    key: `u-${id}`,
    id,
    name: member.User?.Name || member.UserNameFull || member.UserName || `User ${id}`,
    detail: "Unavailable member",
    company: member.User?.CompanyName ?? "",
    isGroup: false,
    unavailable: true,
  };
}

function groupMemberOption(member: TriyoProjectMember | TriyoProjectGroupMember): DealPersonOption | null {
  const id = member.GroupId ?? member.Group?.GroupId ?? member.Group?.Id;
  if (!id) return null;
  return {
    key: `g-${id}`,
    id,
    name: member.Group?.Name || `Group ${id}`,
    detail: "Unavailable group",
    company: member.Group?.CompanyName ?? "",
    isGroup: true,
    unavailable: true,
  };
}

function ownerOption(owner: TriyoProjectOwner): DealPersonOption | null {
  if (!owner.Id) return null;
  return owner.IsGroup
    ? {
        key: `g-${owner.Id}`,
        id: owner.Id,
        name: owner.Group?.Name || `Group ${owner.Id}`,
        detail: "Unavailable group",
        company: owner.Group?.CompanyName ?? "",
        isGroup: true,
        unavailable: true,
      }
    : {
        key: `u-${owner.Id}`,
        id: owner.Id,
        name: owner.User?.Name || `User ${owner.Id}`,
        detail: "Unavailable member",
        company: owner.User?.CompanyName ?? "",
        isGroup: false,
        unavailable: true,
      };
}

/**
 * Adds assigned users/groups that the current roster no longer offers. The Web
 * edit form preserves these orphaned rows because the API replaces membership
 * from the submitted list.
 */
export function buildEditRoster(project: TriyoProject, roster: ProjectsUsersAndGroups | null): DealPersonOption[] {
  const byKey = new Map(normalizeRoster(roster).map((option) => [option.key, option]));
  const assigned = [
    ...(project.Members ?? []).map((member) =>
      member.IsGroup || member.GroupId ? groupMemberOption(member) : userMemberOption(member)
    ),
    ...(project.Groups ?? []).map(groupMemberOption),
    ...(project.Owners ?? []).map(ownerOption),
  ];

  for (const option of assigned) {
    if (option && !byKey.has(option.key)) byKey.set(option.key, option);
  }

  return [...byKey.values()].sort((left, right) => {
    if (left.unavailable !== right.unavailable) return left.unavailable ? 1 : -1;
    if (left.isGroup !== right.isGroup) return left.isGroup ? 1 : -1;
    return left.name.localeCompare(right.name);
  });
}

function memberKey(member: TriyoProjectMember): string | null {
  if (member.IsGroup || member.GroupId) {
    const id = member.GroupId ?? member.Group?.GroupId ?? member.Group?.Id;
    return id ? `g-${id}` : null;
  }
  const id = member.UserId ?? member.User?.UserId ?? member.User?.Id;
  return id ? `u-${id}` : null;
}

function groupKey(group: TriyoProjectGroupMember): string | null {
  const id = group.GroupId ?? group.Group?.GroupId ?? group.Group?.Id;
  return id ? `g-${id}` : null;
}

function groupContainsUser(
  entry: TriyoProjectMember | TriyoProjectGroupMember | TriyoProjectOwner,
  userId: number
): boolean {
  const members = "Members" in entry ? entry.Members : undefined;
  return (members ?? entry.Group?.Members ?? []).some((member) => (member.UserId ?? member.Id) === userId);
}

/** Mirrors the Web member picker's edit gate, which uses the initial roster. */
export function isInitialProjectMember(project: TriyoProject, userId: number | undefined): boolean {
  if (!userId) return false;
  const inMembers = (project.Members ?? []).some((member) => {
    if (member.IsGroup || member.GroupId) return groupContainsUser(member, userId);
    return (member.UserId ?? member.User?.UserId ?? member.User?.Id) === userId;
  });
  return inMembers || (project.Groups ?? []).some((group) => groupContainsUser(group, userId));
}

/** Mirrors the Web owner control gate, including ownership through a group. */
export function isInitialProjectOwner(project: TriyoProject, userId: number | undefined): boolean {
  if (!userId) return false;
  if (!project.IsMultiOwner && (project.OwnerId ?? project.Owner?.Id) === userId) return true;

  if (
    (project.Owners ?? []).some((owner) => (owner.IsGroup ? groupContainsUser(owner, userId) : owner.Id === userId))
  ) {
    return true;
  }

  const ownedMember = (project.Members ?? []).some((member) => {
    if (!member.IsOwner) return false;
    return member.IsGroup || member.GroupId
      ? groupContainsUser(member, userId)
      : (member.UserId ?? member.User?.UserId ?? member.User?.Id) === userId;
  });
  return (
    ownedMember ||
    (project.Groups ?? []).some((group) => (group.IsOwner || group.isOwner) && groupContainsUser(group, userId))
  );
}

export function singleProjectOwnerId(project: TriyoProject): number | null {
  const value = project.OwnerId ?? project.Owner?.Id ?? project.Owners?.find((owner) => !owner.IsGroup)?.Id;
  return typeof value === "number" && value > 0 ? value : null;
}

/** Builds the Web edit form's initial member and multi-owner selections. */
export function buildEditTeamSelection(project: TriyoProject): DealTeamSelection {
  const memberKeys = new Set<string>();
  const ownerKeys = new Set<string>();

  for (const member of project.Members ?? []) {
    const key = memberKey(member);
    if (key) memberKeys.add(key);
    if (key && member.IsOwner) ownerKeys.add(key);
  }
  for (const group of project.Groups ?? []) {
    const key = groupKey(group);
    if (key) memberKeys.add(key);
    if (key && (group.IsOwner || group.isOwner)) ownerKeys.add(key);
  }
  for (const owner of project.Owners ?? []) {
    const key = `${owner.IsGroup ? "g" : "u"}-${owner.Id}`;
    ownerKeys.add(key);
    memberKeys.add(key);
  }
  if (ownerKeys.size === 0 && project.OwnerId) {
    const key = `u-${project.OwnerId}`;
    ownerKeys.add(key);
    memberKeys.add(key);
  }

  return { memberKeys, ownerKeys };
}

function statusId(project: TriyoProject): number {
  if (typeof project.StatusId === "number") return project.StatusId;
  const status = (project.StrStatus || project.StatusName || project.Status || "").toLowerCase();
  if (status === "archived") return 0;
  if (status === "completed") return 2;
  return 1;
}

function priority(value: string | undefined): DealPriority {
  return PRIORITIES.includes(value as DealPriority) ? (value as DealPriority) : "Medium";
}

export interface BuildEditDealPayloadInput {
  project: TriyoProject;
  name: string;
  description: string;
  start: string;
  end: string;
  priority: DealPriority;
  options: DealPersonOption[];
  team: DealTeamSelection;
  dynamicFields: TriyoDynamicField[];
}

/** Builds the full replace-members edit contract used by Triyo.Web. */
export function buildEditDealPayload(input: BuildEditDealPayloadInput): EditProjectRequest {
  const id = getDealId(input.project);
  if (!id) throw new Error("This Deal does not have a valid id.");

  const selectedOwners = [...input.team.ownerKeys]
    .map((key) => input.options.find((option) => option.key === key))
    .filter((option): option is DealPersonOption => option !== undefined);
  const isMultiOwner = input.project.IsMultiOwner === true;
  const owners = isMultiOwner ? selectedOwners : selectedOwners.slice(0, 1);
  const memberByKey = new Map<string, DealPersonOption>();
  for (const person of [...owners, ...input.options.filter((option) => input.team.memberKeys.has(option.key))]) {
    memberByKey.set(person.key, person);
  }
  const firstUserOwner = owners.find((owner) => !owner.isGroup);
  const members = [...memberByKey.values()].map((member) =>
    member.isGroup ? { GroupId: member.id, IsGroup: true } : { UserId: member.id }
  );
  const dynamicFields = buildDynamicFieldsPayload(input.dynamicFields, id);

  return {
    Id: id,
    Name: input.name.trim(),
    Description: input.description.trim(),
    Start: toProjectDateTime(input.start),
    End: toProjectDateTime(input.end),
    StatusId: statusId(input.project),
    Status: input.project.Status,
    StrStatus: input.project.StrStatus,
    Priority: priority(input.priority),
    ...(firstUserOwner
      ? { OwnerId: firstUserOwner.id }
      : input.project.OwnerId
        ? { OwnerId: input.project.OwnerId }
        : {}),
    Owners: owners.map((owner) => ({ Id: owner.id, IsGroup: owner.isGroup })),
    Members: members,
    IsMultiOwner: isMultiOwner,
    CreatedById: input.project.CreatedById,
    Created: input.project.Created,
    ParentTemplateId: input.project.ParentTemplateId,
    ParentTemplateName: input.project.ParentTemplateName,
    HasClient: input.project.HasClient,
    ClientName: input.project.ClientName,
    ClientId: input.project.ClientId,
    Tags: input.project.Tags,
    Documents: input.project.Documents,
    IsCreateNewUserTasks: input.project.IsCreateNewUserTasks,
    IsCreateNewUserTasksCurrent: input.project.IsCreateNewUserTasksCurrent,
    SelectedUsers: members.flatMap((member) => (member.UserId ? [member.UserId] : [])),
    ...(dynamicFields.length > 0 ? { dynamicFields } : {}),
  };
}

export function projectPriority(project: TriyoProject): DealPriority {
  return priority(project.Priority);
}
